import { call, put, take, fork, cancel } from 'redux-saga/effects'
import { eventChannel } from 'redux-saga'
import { singular } from 'pluralize'

export const createCrudSagas = (name, Actions) => {
  const crudSagas = {}

  const pluralSuffix = name.charAt(0).toUpperCase() + name.slice(1)
  const singularSuffix = name.charAt(0).toUpperCase() + singular(name).slice(1)

  const parsePayload = (payload) =>
    payload && (payload.data || payload.params || payload.id || payload.query)
      ? [payload.data || payload.id, payload.params || { query: payload.query }]
      : [payload, undefined]

  crudSagas['find' + pluralSuffix] = function* (api, { payload }) {
    try {
      const [_, params] = parsePayload(payload)

      const response = yield call(api.find, name, params)
      yield put(Actions.findSuccess(response))
    } catch (err) {
      yield put(Actions.findFailure(err.message))
    }
  }

  crudSagas['get' + singularSuffix] = function* (api, { payload }) {
    try {
      const [id, params] = parsePayload(payload)

      const response = yield call(api.get, name, id, params)

      yield put(Actions.getSuccess(response))
    } catch (err) {
      yield put(Actions.getFailure(err.message))
    }
  }

  crudSagas['create' + singularSuffix] = function* (api, { payload }) {
    try {
      const [data, params] = parsePayload(payload)

      const response = yield call(api.create, name, data, params)

      yield put(Actions.createSuccess(response))
    } catch (err) {
      yield put(Actions.createFailure(err.message))
    }
  }

  crudSagas['update' + singularSuffix] = function* (api, { payload }) {
    try {
      const [data, params] = parsePayload(payload)

      const response = yield call(api.patch, name, data._id, data, params)

      yield put(Actions.updateSuccess(response))
    } catch (err) {
      yield put(Actions.updateFailure(err.message))
    }
  }

  crudSagas['delete' + singularSuffix] = function* (api, { payload }) {
    try {
      const [data, params] = parsePayload(payload)

      const response = yield call(api.remove, name, data._id, params)

      yield put(Actions.deleteSuccess(response))
    } catch (err) {
      yield put(Actions.deleteFailure(err.message))
    }
  }

  const createServiceChannel = (service) => {
    return eventChannel((emit) => {
      const createdHandler = (msg) => emit({ event: 'created', payload: msg })
      const updatedHandler = (msg) => emit({ event: 'updated', payload: msg })
      const removedHandler = (msg) => emit({ event: 'removed', payload: msg })
      
      service.on('created', createdHandler)
      service.on('updated', updatedHandler)
      service.on('patched', updatedHandler)
      service.on('removed', removedHandler)

      const unsubscribe = () => {
        service.off('created', createdHandler)
        service.off('updated', updatedHandler)
        service.off('patched', updatedHandler)
        service.off('removed', removedHandler)
      }

      return unsubscribe
    })
  }

  const watchServiceChannel = function* (api) {
    const service = yield call(api.service, name)
    const channel = yield call(createServiceChannel, service)

    while (true) {
      try {
        // An error from socketChannel will cause the saga jump to the catch block
        const msg = yield take(channel)
        switch (msg.event) {
          case 'created':
            yield put(Actions.createSuccess(msg.payload))
            break
          case 'updated':
          case 'patched':
            yield put(Actions.updateSuccess(msg.payload))
            break
          case 'removed':
            yield put(Actions.deleteSuccess(msg.payload))
            break
          default:
            break
        }
      } catch (err) {
        console.error('service error:', err)
      }
    }
  }

  const createSocketChannel = (io) => {
    return eventChannel((emit) => {
      io.on('reconnect', (params) => emit({ event: 'reconnect', params }))

      const unsubscribe = () => {}

      return unsubscribe
    })
  }

  const watchSocketChannel = function* (api) {
    const channel = yield call(createSocketChannel, api.io)
    while (true) {
      try {
        const msg = yield take(channel)
        switch (msg.event) {
          case 'reconnect':
            yield put(Actions.findRequest())
            break
          default:
            break
        }
      } catch (err) {
        console.error('service error:', err)
      }
    }
  }

  let serviceChannelTask;
  let socketChannelTask;

  crudSagas['watch' + pluralSuffix] = function* (api, findOnReconnect) {
    
    serviceChannelTask = yield fork(watchServiceChannel, api)
    if (findOnReconnect)
      socketChannelTask = yield fork(watchSocketChannel, api)

    yield call(api.register, name, 'create')
    yield call(api.register, name, 'patch')
    yield call(api.register, name, 'update')
    yield call(api.register, name, 'remove')
  }

  crudSagas['unwatch' + pluralSuffix] = function* (api, findOnReconnect) {

    yield call(api.unregister, name, 'created')
    yield call(api.unregister, name, 'patch')
    yield call(api.unregister, name, 'update')
    yield call(api.unregister, name, 'remove') 

    if (serviceChannelTask) {
      yield cancel(serviceChannelTask);
    }
    
    if (socketChannelTask) {
      yield cancel(socketChannelTask);
    }
  }

  return crudSagas
}
